import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Zap, Brain, Video, Scissors, CheckCircle, AlertCircle, Coffee } from 'lucide-react';

interface SmartProgressIndicatorProps {
  progress: number; // 0-100
  status: string;
  videoSize?: number; // in bytes
  videoDuration?: number; // in seconds
  stage?: 'upload' | 'analysis' | 'processing' | 'clips' | 'complete' | 'error';
  className?: string;
}

interface ProgressStage {
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  tips: string[];
  estimatedTimeMs: number;
  color: string;
}

const SmartProgressIndicator: React.FC<SmartProgressIndicatorProps> = ({
  progress,
  status,
  videoSize = 0,
  videoDuration = 0,
  stage = 'processing',
  className = ''
}) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Calculate realistic time estimates based on video characteristics
  const timeEstimates = useMemo(() => {
    const sizeGB = videoSize / (1024 * 1024 * 1024);
    const durationMin = videoDuration / 60;
    
    // Base estimates in seconds
    let uploadTime = Math.max(30, sizeGB * 120); // 2 min per GB
    let analysisTime = Math.max(60, durationMin * 15); // 15 sec per minute of video
    let clipsTime = Math.max(30, durationMin * 5); // 5 sec per minute
    
    // Adjust for server load (add 20-50% buffer)
    const loadFactor = 1.3;
    uploadTime *= loadFactor;
    analysisTime *= loadFactor;
    clipsTime *= loadFactor;
    
    return {
      upload: uploadTime,
      analysis: analysisTime,
      clips: clipsTime,
      total: uploadTime + analysisTime + clipsTime
    };
  }, [videoSize, videoDuration]);

  // Define progress stages with context-aware information
  const progressStages: Record<string, ProgressStage> = {
    upload: {
      icon: Video,
      title: 'Uploading Your Video',
      description: 'Securely transferring your content to our servers',
      tips: [
        'Large videos take longer to upload - this is normal!',
        'Your upload speed depends on your internet connection',
        'We use chunked uploads for reliability',
        'You can safely close this tab - upload will continue'
      ],
      estimatedTimeMs: timeEstimates.upload * 1000,
      color: 'from-gray-600 to-gray-700'
    },
    analysis: {
      icon: Brain,
      title: 'AI Content Analysis',
      description: 'Our AI is analyzing your video for exciting moments',
      tips: [
        'Detecting audio spikes and volume changes',
        'Analyzing facial expressions and emotions', 
        'Finding scene transitions and cuts',
        'Identifying highlights and peak moments',
        'Gaming content typically processes faster',
        'Reaction videos have great highlight detection'
      ],
      estimatedTimeMs: timeEstimates.analysis * 1000,
      color: 'from-red-500 to-red-600'
    },
    processing: {
      icon: Zap,
      title: 'Processing Video Content',
      description: 'Extracting highlights and preparing clips',
      tips: [
        'Creating timeline markers for highlights',
        'Preparing video segments for clipping',
        'Optimizing quality for each platform',
        'This step ensures smooth playback',
        'Higher resolution videos take longer'
      ],
      estimatedTimeMs: timeEstimates.clips * 1000,
      color: 'from-red-500 to-red-600'
    },
    clips: {
      icon: Scissors,
      title: 'Generating Clips',
      description: 'Creating your viral-ready clips from highlights',
      tips: [
        'Each clip is optimized for maximum engagement',
        'Clips include perfect timing and transitions',
        'Multiple export formats are being prepared',
        'TikTok and YouTube Shorts formats included'
      ],
      estimatedTimeMs: timeEstimates.clips * 1000,
      color: 'from-gray-700 to-gray-900'
    },
    complete: {
      icon: CheckCircle,
      title: 'All Done!',
      description: 'Your clips are ready to download and share',
      tips: [
        'Check out your generated clips below',
        'Export to your favorite social platforms',
        'Each clip is optimized for viral potential'
      ],
      estimatedTimeMs: 0,
      color: 'from-gray-800 to-black'
    },
    error: {
      icon: AlertCircle,
      title: 'Processing Error',
      description: 'Something went wrong, but we can fix this',
      tips: [
        'Try refreshing the page and checking again',
        'Large files sometimes need a retry',
        'Contact support if the issue persists',
        'Your video file is safely stored'
      ],
      estimatedTimeMs: 0,
      color: 'from-red-500 to-red-600'
    }
  };

  const currentStage = progressStages[stage] || progressStages.processing;

  // Cycle through tips every 4 seconds
  useEffect(() => {
    if (currentStage.tips.length > 1) {
      const interval = setInterval(() => {
        setCurrentTipIndex(prev => (prev + 1) % currentStage.tips.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [currentStage.tips.length]);

  // Track elapsed time
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate time remaining
  const timeRemainingMs = Math.max(0, currentStage.estimatedTimeMs - elapsedTime);
  const timeRemainingStr = useMemo(() => {
    const seconds = Math.ceil(timeRemainingMs / 1000);
    if (seconds < 60) return `${seconds}s remaining`;
    const minutes = Math.ceil(seconds / 60);
    return `~${minutes}m remaining`;
  }, [timeRemainingMs]);

  // Format file size for display
  const fileSizeStr = useMemo(() => {
    if (!videoSize) return '';
    const mb = videoSize / (1024 * 1024);
    const gb = mb / 1024;
    return gb >= 1 ? `${gb.toFixed(1)}GB` : `${mb.toFixed(0)}MB`;
  }, [videoSize]);

  // Format video duration for display
  const durationStr = useMemo(() => {
    if (!videoDuration) return '';
    const minutes = Math.floor(videoDuration / 60);
    const seconds = videoDuration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [videoDuration]);

  const Icon = currentStage.icon;

  return (
    <div className={`bg-white rounded-xl p-6 shadow-lg border border-gray-200 ${className}`}>
      {/* Header with icon and title */}
      <div className="flex items-center space-x-4 mb-4">
        <div className={`w-12 h-12 bg-gradient-to-r ${currentStage.color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{currentStage.title}</h3>
          <p className="text-sm text-gray-600">{currentStage.description}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span className="font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-lg h-3 overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${currentStage.color} transition-all duration-500 ease-out relative ${progress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          >
            {/* Shimmer effect for active progress */}
            {progress < 100 && stage !== 'error' && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse" />
            )}
          </div>
        </div>
      </div>

      {/* Video info and time estimate */}
      <div className="flex justify-between items-center mb-4 text-sm">
        <div className="flex items-center space-x-4 text-gray-600">
          {fileSizeStr && (
            <span className="flex items-center space-x-1">
              <Video className="w-4 h-4" />
              <span>{fileSizeStr}</span>
            </span>
          )}
          {durationStr && (
            <span className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{durationStr}</span>
            </span>
          )}
        </div>
        {stage !== 'complete' && stage !== 'error' && timeRemainingMs > 0 && (
          <span className="text-gray-500 flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{timeRemainingStr}</span>
          </span>
        )}
      </div>

      {/* Rotating tips */}
      {currentStage.tips.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-gray-600 text-xs font-bold">💡</span>
            </div>
            <div className="min-h-[3rem] flex items-center">
              <p className="text-sm text-gray-700 leading-relaxed">
                {currentStage.tips[currentTipIndex]}
              </p>
            </div>
          </div>
          
          {/* Tip progress dots */}
          {currentStage.tips.length > 1 && (
            <div className="flex justify-center space-x-2 mt-3">
              {currentStage.tips.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentTipIndex ? 'bg-red-500 scale-110' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Status message if provided */}
      {status && status !== 'processing' && (
        <div className="mt-4 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-100">
          <span className="font-medium">Status:</span> {status}
        </div>
      )}

      {/* Fun waiting message for long processes */}
      {elapsedTime > 60000 && stage === 'analysis' && (
        <div className="mt-4 text-center">
          <div className="flex items-center justify-center space-x-2 text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-200">
            <Coffee className="w-5 h-5" />
            <span className="text-sm font-medium">
              This is taking a bit longer than usual - grab a coffee! ☕
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartProgressIndicator; 